/**
 * Accessible Video Handler for ERB-rendered lazyloaded videos
 * Respects prefers-reduced-motion user preference (WCAG 2.2.2 & 2.3.3)
 */

const prefersReducedMotion = () => {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const createPlayPauseButton = (video) => {
  const button = document.createElement('button');
  button.className = 'accessible-video-control';
  button.setAttribute('aria-label', 'Play video');
  button.innerHTML = '<span class="play-icon">▶</span><span class="pause-icon">⏸</span>';
  button.setAttribute('data-playing', 'false');

  button.addEventListener('click', () => {
    if (video.paused) {
      video.play();
      button.setAttribute('data-playing', 'true');
      button.setAttribute('aria-label', 'Pause video');
    } else {
      video.pause();
      button.setAttribute('data-playing', 'false');
      button.setAttribute('aria-label', 'Play video');
    }
  });

  return button;
};

const setupAccessibleVideo = (video) => {
  // Skip if already processed
  if (video.dataset.accessibleProcessed) return;
  video.dataset.accessibleProcessed = 'true';

  const shouldReduceMotion = prefersReducedMotion();
  const hasAutoplay = video.hasAttribute('autoplay');

  // If reduced motion is preferred, remove autoplay and pause the video
  if (shouldReduceMotion && hasAutoplay) {
    video.removeAttribute('autoplay');
    video.pause();
  }

  // Wrap the video in a container if it doesn't already have one
  const parent = video.parentElement;
  if (!parent.classList.contains('accessible-video-wrapper')) {
    const wrapper = document.createElement('div');
    wrapper.className = 'accessible-video-wrapper';
    parent.insertBefore(wrapper, video);
    wrapper.appendChild(video);

    // Add play/pause control button for videos without native controls
    if (!video.hasAttribute('controls') || shouldReduceMotion) {
      const button = createPlayPauseButton(video);
      wrapper.appendChild(button);

      // Update button state when video plays/pauses
      video.addEventListener('play', () => {
        button.setAttribute('data-playing', 'true');
        button.setAttribute('aria-label', 'Pause video');
      });

      video.addEventListener('pause', () => {
        button.setAttribute('data-playing', 'false');
        button.setAttribute('aria-label', 'Play video');
      });

      // If video should autoplay and reduced motion is not preferred, update button
      if (hasAutoplay && !shouldReduceMotion) {
        video.addEventListener('loadeddata', () => {
          if (!video.paused) {
            button.setAttribute('data-playing', 'true');
            button.setAttribute('aria-label', 'Pause video');
          }
        });
      }
    }
  }
};

const initAccessibleVideos = () => {
  // Handle existing videos
  const videos = document.querySelectorAll('video.lazyload, video[autoplay]');
  videos.forEach(setupAccessibleVideo);

  // Watch for dynamically added videos using MutationObserver
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.tagName === 'VIDEO' && (node.classList.contains('lazyload') || node.hasAttribute('autoplay'))) {
            setupAccessibleVideo(node);
          }
          // Also check children
          const childVideos = node.querySelectorAll?.('video.lazyload, video[autoplay]');
          childVideos?.forEach(setupAccessibleVideo);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Listen for lazysizes lazyloaded event
  document.addEventListener('lazyloaded', (e) => {
    if (e.target.tagName === 'VIDEO') {
      setupAccessibleVideo(e.target);
    }
  });
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccessibleVideos);
} else {
  initAccessibleVideos();
}

// Listen for changes to reduced motion preference
if (window.matchMedia) {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const handleChange = () => {
    // Re-process all videos when preference changes
    const videos = document.querySelectorAll('video[data-accessible-processed="true"]');
    videos.forEach((video) => {
      if (prefersReducedMotion()) {
        video.pause();
      }
    });
  };

  // Use addEventListener if available (modern browsers), fall back to addListener (older Safari/iOS)
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleChange);
  } else if (mediaQuery.addListener) {
    mediaQuery.addListener(handleChange);
  }
}

export { initAccessibleVideos, prefersReducedMotion };
